import React, { useState } from "react";
import {
  FaChevronDown,
  FaChevronRight,
  FaBars,
  FaTimes,
  FaArrowLeft,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import { Product } from "../Types/Types";
import { useTranslation } from "react-i18next";
import LanguageSelector from "./LanguageSelector";



type Subcategory = {
  name: { [key: string]: string };  // Same as above
  products: Product[];
};

type ProductCategory = {
  _id: string;
  name: string;
  subcategories: { name: string; products: any[] }[];
  products: any[];
};




interface NavbarProps {
  products: ProductCategory[];
  lang: string;

}

const Navbar: React.FC<NavbarProps> = ({ products, lang }) => {
  const { t } = useTranslation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProductsSidebarOpen, setIsProductsSidebarOpen] = useState(false);
  const [isWhoWeAreSidebarOpen, setIsWhoWeAreSidebarOpen] = useState(false);

  const [navStack, setNavStack] = useState<{ title: string; items: any[]; type: "category" | "subcategory" | "products" | "who-we-are"; }[]>([]);

  const openProductsSidebar = () => {
    setNavStack([
      { title: "Categories", items: sortedProducts, type: "category" },
    ]);
    setIsMobileMenuOpen(false);
    setIsProductsSidebarOpen(true);
  };

  const openWhoWeAreSidebar = () => {
    setNavStack([
      {
        title: 'Who we are',
        items: [
          { label: t("navbar.aboutUs"), path: "about" },
          { label: t("navbar.history"), path: "history" }
        ],
        type: "who-we-are"
      }
    ]);
    setIsMobileMenuOpen(false);
    setIsWhoWeAreSidebarOpen(true);
  };


  const handleBack = () => {
    if (navStack.length > 1) {
      setNavStack((prev) => prev.slice(0, -1));
    } else {
      setIsProductsSidebarOpen(false);
      setIsWhoWeAreSidebarOpen(false);
    }
  };

  const handleCategoryClick = (category: ProductCategory) => {
    if (category.subcategories.length > 0) {
      setNavStack((prev) => [
        ...prev,
        {
          title: category.name || category.name, // Use lang or fallback to "en"
          items: category.subcategories,
          type: "subcategory",
        },
      ]);
    } else if (category.products.length > 0) {
      setNavStack((prev) => [
        ...prev,
        {
          title: category.name || category.name, // Use lang or fallback to "en"
          items: category.products,
          type: "products",
        },
      ]);
    }
  };

  const handleSubcategoryClick = (subcategory: Subcategory) => {
    if (subcategory.products.length > 0) {
      setNavStack((prev) => [
        ...prev,
        {
          title: subcategory.name[lang] || subcategory.name["en"], // Use lang or fallback to "en"
          items: subcategory.products,
          type: "products",
        },
      ]);
    }
  };

  // const sortedProducts = [...products]; // Just copy the products array without sorting
  const currentLevel = navStack[navStack.length - 1];


  const nameToKeyMap: Record<string, string> = {
    "Medicinal Products": "medicinal",
    "CBD Products": "cbd",
    "Herbal Products": "herbal",
    "Dietary Supplements": "dietary",
    "Personal Care Products": "personalCare",
  };

  const order = ["medicinal", "cbd", "herbal", "dietary", "personalCare"];

  const sortedProducts = [...products].sort(
    (a, b) =>
      order.indexOf(nameToKeyMap[a.name]) -
      order.indexOf(nameToKeyMap[b.name])
  );

  const subcategoryTranslations: Record<string, string> = {
    "OTC": "otc",
    "Oral": "oral",
    "Topical": "topical",
    "Cosmetics": "cosmetics",
    "GALAHEALTH": "galahealth",
    "JEKOGAL": "jekogal",
    "GALAPRO": "galapro",
    "BABYGAL Baby Skin Care": "babyGalSkinCare",
    "SENSILIS MAM CARE": "sensilisMamCare",
    "FACE & LIP CARE": "face&Lip",
    "SENSILIS FOOT CARE": "sensilisFootCare",
    "SENSILIS NAIL & HAND CARE": "sensilisNail",
    "SENSILIS OILS": "sensilisOils",
    "SILK HAIR REMOVAL": "sensilisHair",
    "SOLARUS SUN PROTECTION CARE": "solarus",
    "CITROGALA INSPECT PROTECTION": "citrogala"
  };

  const orderSubcategory = [
    "otc", "oral", "topical", "cosmetics", "galahealth", "jekogal", "galapro",
    "babyGalSkinCare", "sensilisMamCare", "face&Lip", "sensilisFootCare",
    "sensilisNail", "sensilisOils", "sensilisHair", "solarus", "citrogala"
  ];


  return (
    <div className="bg-white h-16 flex items-center shadow-sm border-b border-gray-100 fixed top-0 w-full z-[9999]">
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-5">
        {/* Hamburger */}
        <div className="md:hidden flex items-center">
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="text-brand-gray text-2xl transition-colors duration-200 hover:text-brand-500"
            aria-label="Menu"
          >
            {isMobileMenuOpen ? <FaTimes /> : <FaBars />}
          </button>
        </div>

        {/* Logo */}
        <div className="flex flex-1 justify-center">
          <Link to="/">
            {lang === 'mk' ?
              <img src="/LOGO MK POSLEDNO.png" alt="Galafarm Logo" className="h-11 w-auto" />
              :
              <img src="/LOGO GB POSLEDNO.png" alt="Galafarm Logo" className="h-11 w-auto" />
            }
          </Link>
        </div>

        <LanguageSelector />

      </div>

      {/* Main Mobile Sidebar */}
      <div
        className={`md:hidden fixed top-0 left-0 w-full h-full bg-white z-50 transition-transform duration-300 ease-in-out ${isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
          }`}
      >
        <div className="relative p-6 pt-24 overflow-y-auto h-full">
          {/* Close X Button */}
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="absolute top-6 right-6 text-2xl text-brand-gray transition-colors duration-200 hover:text-brand-500"
            aria-label="Close"
          >
            <FaTimes />
          </button>

          <ul className="space-y-2 text-lg text-brand-gray">
            <li>
              <button
                onClick={openWhoWeAreSidebar}
                className="w-full flex items-center justify-between py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
              >
                <span>{t(`navbar.whoWeAre`)}</span>
                <FaChevronRight className="text-sm text-brand-400" />
              </button>
            </li>
            <li>
              <button
                onClick={openProductsSidebar}
                className="w-full flex items-center justify-between py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
              >
                <span>{t(`navbar.products`)}</span>
                <FaChevronRight className="text-sm text-brand-400" />
              </button>
            </li>
            <li>
              <Link
                to="/news"
                className="block py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t(`navbar.news`)}
              </Link>
            </li>
            <li>
              <Link
                to="/blog"
                className="block py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t(`navbar.health`)}
              </Link>
            </li>
            <li>
              <Link
                to="/careers"
                className="block py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t(`navbar.careers`)}
              </Link>
            </li>
            <li>
              <Link
                to="/contacts"
                className="block py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t(`navbar.contacts`)}
              </Link>
            </li>
          </ul>
        </div>
      </div>

      {/* Who We Are Sidebar */}
      <div
        className={`md:hidden fixed top-0 left-0 w-full h-full bg-white z-50 transition-transform duration-300 ease-in-out ${isWhoWeAreSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="relative p-6 pt-24 overflow-y-auto h-full">
          {/* Close X Button */}
          <button
            onClick={() => setIsWhoWeAreSidebarOpen(false)}
            className="absolute top-6 right-6 text-2xl text-brand-gray transition-colors duration-200 hover:text-brand-500"
            aria-label="Close"
          >
            <FaTimes />
          </button>

          {/* Back Button */}
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm font-semibold text-brand-500 transition-colors duration-200 hover:text-brand-600"
            >
              <FaArrowLeft />
              <span>{t(`navbar.whoWeAre`)}</span>
            </button>
          </div>

          <ul className="space-y-1 text-base text-brand-gray">
            {currentLevel?.type === "who-we-are" &&
              currentLevel.items.map((item, index) => (
                <li key={index}>
                  <Link
                    to={`/${item.path}`}
                    className="block py-3 px-2 rounded-lg transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                    onClick={() => setIsWhoWeAreSidebarOpen(false)}
                  >
                    {item.label}
                  </Link>
                </li>
              ))}
          </ul>
        </div>
      </div>

      {/* Products Sidebar */}
      <div
        className={`md:hidden fixed top-0 left-0 w-full h-full bg-white z-50 transition-transform duration-300 ease-in-out ${isProductsSidebarOpen ? "translate-x-0" : "translate-x-full"
          }`}
      >
        <div className="relative p-6 pt-24 overflow-y-auto h-full">
          {/* Close X Button */}
          <button
            onClick={() => setIsProductsSidebarOpen(false)}
            className="absolute top-6 right-6 text-2xl text-brand-gray transition-colors duration-200 hover:text-brand-500"
            aria-label="Close"
          >
            <FaTimes />
          </button>

          {/* Back Button */}
          <div className="flex items-center mb-6">
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-sm font-semibold text-brand-500 transition-colors duration-200 hover:text-brand-600"
            >
              <FaArrowLeft />
              <span>{t(`navbar.products`)}</span>
            </button>
          </div>

          <ul className="space-y-1 text-base text-brand-gray">
            {currentLevel?.type === "category" &&
              currentLevel.items.map((cat: ProductCategory) => (
                <li key={cat._id}>
                  <button
                    onClick={() => handleCategoryClick(cat)}
                    className="w-full flex items-center justify-between py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                  >
                    <span>{t(`products.${nameToKeyMap[cat.name]}`)}</span>

                    {(cat.subcategories.length > 0 || cat.products.length > 0) && (
                      <FaChevronRight className="text-sm text-brand-400" />
                    )}
                  </button>
                </li>
              ))}

            {currentLevel?.type === "subcategory" &&
              [...currentLevel.items]
                .sort((a: any, b: any) => {
                  const aLabel =
                    typeof a.name === "string"
                      ? a.name
                      : a.name?.[lang] || a.name?.["en"] || "";
                  const bLabel =
                    typeof b.name === "string"
                      ? b.name
                      : b.name?.[lang] || b.name?.["en"] || "";

                  const aKey = subcategoryTranslations[aLabel];
                  const bKey = subcategoryTranslations[bLabel];

                  return (
                    orderSubcategory.indexOf(aKey) - orderSubcategory.indexOf(bKey)
                  );
                })
                .map((sub: any) => {
                  const label =
                    typeof sub.name === "string"
                      ? sub.name
                      : sub.name?.[lang] || sub.name?.["en"] || "Unnamed";


                  return (
                    <li key={label}>
                      <button
                        onClick={() =>
                          handleSubcategoryClick({
                            name:
                              typeof sub.name === "string"
                                ? { en: sub.name }
                                : sub.name,
                            products: sub.products,
                          })
                        }
                        className="w-full flex items-center justify-between py-3 px-2 rounded-lg font-semibold transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                      >
                        <span>{t(`subcategories.${sub.name}`)}</span>
                        {sub.products.length > 0 && (
                          <FaChevronRight className="text-sm text-brand-400" />
                        )}
                      </button>
                    </li>
                  );
                })}


            {currentLevel?.type === "products" &&
              currentLevel.items.map((product: Product) => (
                <li key={product._id}>
                  <Link
                    to={`/product/${product._id}`}
                    className="block py-3 px-2 rounded-lg transition-colors duration-200 hover:bg-brand-50 hover:text-brand-600"
                    onClick={() => setIsProductsSidebarOpen(false)}
                  >
                    {product.name[lang] || product.name["en"]} {/* Display name based on lang */}
                  </Link>
                </li>
              ))}
          </ul>

        </div>
      </div>
    </div>
  );
};

export default Navbar;
